// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — colour-key transparency for picture streams (DSA
 * `layers[].alphaMode: "color_key"` with `layers[].colorKey`).
 *
 * A plain 2:1 equirectangular RGB stream (H.264, no alpha plane) marks
 * the places it has no picture with one colour — GOES generations: pure
 * black wherever no satellite was looking — and the publisher lifts every
 * real pixel clear of it. The globes turn that colour transparent, so the
 * globe's own Earth shows through, instead of drawing it as a picture.
 * Owner rule: equirectangular streams stay 2:1, which is why this is used
 * rather than stacked alpha.
 *
 * The rule, the same on every surface (browser globe, every globe of a
 * multi-globe layout, real-time overlays, Studio renders, the headset and
 * AR globes):
 *
 *     d     = max(|R − Kr|, |G − Kg|, |B − Kb|)      all in 0..1, as decoded
 *     alpha = smoothstep(tolerance, tolerance + COLOR_KEY_EDGE, d)
 *
 * so a pixel within `tolerance` of the key is clear, one more than
 * `COLOR_KEY_EDGE` beyond it is fully drawn, and the band between is a
 * soft edge that hides H.264's ringing and chroma noise around the
 * coverage edge. The result multiplies the layer's other opacities (the
 * viewer's transparency slider, a Studio fade).
 *
 * Distances are measured on the colour as the browser's decoder hands it
 * to WebGL, in sRGB code space (the headset's textures are sRGB-decoded
 * to linear, so its shaders encode back first). Picture streams are
 * encoded limited ("tv") range, which Chrome expands correctly: black
 * arrives as 0 (±1–2 codes of noise), well inside a 0.04 tolerance. The
 * value-encoded streams' known Chrome issue (full-range frames expanded
 * as if limited, decoderRange.ts) would only push dark pixels *down*: a
 * black key stays black, but a real pixel lifted less than ~0.07 above
 * it could fall into the key there. Publishers lift real pixels to at
 * least `tolerance + COLOR_KEY_EDGE`, and keep picture streams tv range.
 *
 * Streams without `color_key` never reach any of this: no key, no
 * uniform change, the shaders take the branch they always took.
 */

/** A DSA colour key: the colour in 0–255 per channel, the tolerance normalised 0–1. */
export interface ColorKey {
  readonly r: number
  readonly g: number
  readonly b: number
  readonly tolerance: number
}

/** The soft band above the tolerance (≈ 8 codes): wide enough to hide H.264 noise, narrow enough to keep dark real pixels. */
export const COLOR_KEY_EDGE = 0.03

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function channel(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 255 ? value : null
}

/**
 * The colour key of a `.dsa`'s drawn picture layer: the first layer that
 * is not a legend, not hidden, and says `alphaMode: "color_key"` with a
 * valid `colorKey`. Null for everything else (no layer, another alpha
 * mode, a key missing a channel). Tolerance defaults to 0 as the schema
 * says, and is clamped to 0..1. Never throws.
 */
export function parseColorKey(raw: unknown): ColorKey | null {
  const dsa = asRecord(raw)
  const layers = dsa && Array.isArray(dsa.layers) ? dsa.layers : []
  for (const entry of layers) {
    const layer = asRecord(entry)
    if (!layer || layer.isLegend === true || layer.visible === false) continue
    if (layer.alphaMode !== 'color_key') continue
    const key = asRecord(layer.colorKey)
    if (!key) return null
    const r = channel(key.r), g = channel(key.g), b = channel(key.b)
    if (r === null || g === null || b === null) return null
    const t = typeof key.tolerance === 'number' && Number.isFinite(key.tolerance) ? key.tolerance : 0
    return { r, g, b, tolerance: Math.min(1, Math.max(0, t)) }
  }
  return null
}

/** Hermite smoothstep, as GLSL defines it. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

/**
 * The alpha one decoded pixel gets (channels 0–255, as WebGL reads them).
 * The shaders' `colorKeyAlpha` does exactly this; kept here for tests and
 * for anything that counts coverage on the CPU.
 */
export function colorKeyAlpha(r: number, g: number, b: number, key: ColorKey): number {
  const d = Math.max(Math.abs(r - key.r), Math.abs(g - key.g), Math.abs(b - key.b)) / 255
  return smoothstep(key.tolerance, key.tolerance + COLOR_KEY_EDGE, d)
}

/** The shaders' uniform: (r, g, b, tolerance) in 0..1, or w = −1 for "no key". */
export function colorKeyUniform(key: ColorKey | null | undefined): [number, number, number, number] {
  return key ? [key.r / 255, key.g / 255, key.b / 255, key.tolerance] : [0, 0, 0, -1]
}

/** Same key (both absent counts as the same). */
export function sameColorKey(a: ColorKey | null | undefined, b: ColorKey | null | undefined): boolean {
  if (!a || !b) return !a && !b
  return a.r === b.r && a.g === b.g && a.b === b.b && a.tolerance === b.tolerance
}

/**
 * GLSL (ES 1.00 and 3.00 alike): `colorKeyAlpha(rgb, key)` for an sRGB
 * code-space colour and a `colorKeyUniform`; 1.0 when `key.w < 0` (no key).
 */
export const COLOR_KEY_GLSL = `
  float colorKeyAlpha(vec3 rgb, vec4 key) {
    if (key.w < 0.0) return 1.0;
    vec3 d3 = abs(rgb - key.rgb);
    float d = max(d3.r, max(d3.g, d3.b));
    return smoothstep(key.w, key.w + ${COLOR_KEY_EDGE.toFixed(3)}, d);
  }

  // three.js samples an sRGB texture already decoded to linear: encode it
  // back (the exact sRGB curve) so the distance is the one the stream has.
  float colorKeyAlphaLinear(vec3 linearRgb, vec4 key) {
    if (key.w < 0.0) return 1.0;
    vec3 c = clamp(linearRgb, 0.0, 1.0);
    vec3 srgb = mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), c));
    return colorKeyAlpha(srgb, key);
  }
`
